import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { Pill, TableSkeleton } from "../components/Pill";
import {
  PAYMENT_LINK_STATUS_UI,
  PAYMENT_VALIDATION_STATUS_UI,
} from "../lib/enumUI";
import { paymentOrdersQueryOptions } from "./queries";
import type { PaymentOrderEmailSummary } from "./types";

export function PaymentOrderList() {
  const { data, isLoading, error } = useQuery(paymentOrdersQueryOptions);

  if (isLoading) {
    return (
      <div className="page">
        <TableSkeleton />
      </div>
    );
  }
  if (error) {
    const message = error instanceof Error ? error.message : String(error);
    return (
      <div className="page">
        <p className="alert" role="alert">
          Failed to load payment orders: {message}
        </p>
      </div>
    );
  }

  const orders = data?.payment_orders ?? [];

  return (
    <div className="page">
      <h1>Payment orders</h1>
      {orders.length === 0 ? (
        <p className="empty-state">
          No payment-order emails visible under your access.
        </p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Received</th>
              <th>Sender</th>
              <th>Subject</th>
              <th>Project link</th>
              <th>Latest validation</th>
              <th>Last run</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <PaymentOrderRow key={o.id} order={o} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PaymentOrderRow({ order }: { order: PaymentOrderEmailSummary }) {
  return (
    <tr>
      <td>{new Date(order.received_at).toLocaleString()}</td>
      <td>{order.sender}</td>
      <td>
        <Link
          to="/payment-orders/$emailId"
          params={{ emailId: order.id }}
        >
          {order.subject ?? <em>(no subject)</em>}
        </Link>
      </td>
      <td>
        <Pill tone={PAYMENT_LINK_STATUS_UI[order.project_link_status].tone}>
          {PAYMENT_LINK_STATUS_UI[order.project_link_status].label}
        </Pill>
      </td>
      <td>
        {order.latest_validation_status ? (
          <Pill
            tone={
              PAYMENT_VALIDATION_STATUS_UI[order.latest_validation_status].tone
            }
          >
            {PAYMENT_VALIDATION_STATUS_UI[order.latest_validation_status].label}
          </Pill>
        ) : (
          "—"
        )}
      </td>
      <td>
        {order.latest_validation_at
          ? new Date(order.latest_validation_at).toLocaleString()
          : "—"}
      </td>
    </tr>
  );
}
